import { useState } from "react";
import { ERROR_MESSAGES } from "../../constant/constant.js";

/**
 * Custom hook to delete a kid profile.
 * Uses the parent token from parentLocalData in localStorage
 */
export const useDeleteKid = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  /**
   * Deletes a kid profile.
   * @param {number} kidId The ID of the kid to delete.
   * @returns {Promise<any|null>} The response data if successful, null otherwise.
   */
  const deleteKid = async (kidId, customFetch) => {
    setLoading(true);
    setError("");
    setData(null);

    try {
      let response;
      if (customFetch) {
        response = await customFetch(`${apiUrl}/parent/destroy/kid/${kidId}`, {
          method: "DELETE",
        });
      } else {
        const parentLocalData = JSON.parse(
          localStorage.getItem("parentLocalData"),
        );
        const parentToken = parentLocalData?.token;

        if (!parentToken) {
          throw new Error(ERROR_MESSAGES.FORBIDDEN);
        }

        response = await fetch(`${apiUrl}/parent/destroy/kid/${kidId}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${parentToken}`,
          },
        });
      }

      if (!response) {
        throw new Error("Request failed or session expired");
      }

      if (!response.ok) {
        let errorMsg =
          ERROR_MESSAGES?.UNKNOWN || "Failed to delete kid profile";
        try {
          const errorData = await response.json();
          if (errorData?.errors) {
            const errorKey = Object.keys(errorData.errors)[0];
            errorMsg = errorData.errors[errorKey][0];
          } else {
            errorMsg = errorData?.message || errorMsg;
          }
        } catch {
          if (response.status === 500) {
            errorMsg =
              ERROR_MESSAGES?.INTERNAL_SERVER || "Internal server error";
          }
        }
        throw new Error(errorMsg);
      }

      const responseData = await response.json();
      setData(responseData);
      return responseData;
    } catch (err) {
      setError(
        err?.message || ERROR_MESSAGES?.UNKNOWN || "Something went wrong",
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteKid,
    loading,
    error,
    data,
    setError,
  };
};
